
import { Component, Vue, Watch, Prop, ProvideReactive } from "vue-property-decorator"

import { CaseDetails, TemplateDetails, CaseUser } from "@/types"
import { CurrentUser } from "@/types/user"

import api from "@/services/api"
import eventsApi from "@/services/api.event"

import HubCaseService from "@/services/hubCaseService"

import { CaseEvents } from "@/types/originatingEvents"
import { IntegrationOutOption } from "@/types/integration"
import { RecordFieldsUpdateDto } from "@/types/caseUpdate"

import integrationApi from "@/services/api_Integrations"
import { getCanEditClosedCase, getCaseHasMapField } from "@/scripts/caseDetails"

@Component({
	components: {
	}
})
export default class LayoutHost extends Vue {
	@ProvideReactive("hubCaseService") hubCaseService: HubCaseService = new HubCaseService()

	@Prop()
	public currentCase!: CaseDetails | null

	@Prop()
	public me!: CurrentUser

	@Prop()
	currentCaseTemplate!: TemplateDetails | null

	public newCaseTemplate: TemplateDetails | null = null
	public caseEvents: CaseEvents | null = null

	private caseIntegrationLinks: IntegrationOutOption[] = []
	private makingClosedChanges: boolean = false

	private get template (): TemplateDetails | null {
		return this.newCaseTemplate ? this.newCaseTemplate : this.currentCaseTemplate
	}

	public get valid (): boolean {
		return !!this.currentCase && !!this.currentCase.templateId && (this.titleTemplateEnabled ? true : (!!this.currentCase.title && this.currentCase.title.length > 0))
	}

	/**
	 * Returns true if the user had Edit After Close Permissions
	 */
	public get editAfterClosePermissions (): boolean {
		return getCanEditClosedCase(this.currentCaseTemplate, this.me)
	}

	@Watch("valid")
	validChanged (nowValid: boolean) {
		this.$emit("validChanged", nowValid)
	}

	@Watch("currentCase")
	currentCaseChanged (newCase: CaseDetails | null, oldCase: CaseDetails | null) {
		if (newCase?.id && newCase.id === oldCase?.id) {
			// If the case ID hasn't changed, do nothing.
			return
		}

		this.caseEvents = null
		this.caseIntegrationLinks = []

		if (newCase) {
			this.openCaseConnection()

			if (newCase.id) {
				eventsApi.caseEventsGet(newCase.id).then(caseEvents => {
					this.setEvents(caseEvents)
				})

				api.caseIntegrationLinks(newCase.id!).then(integrationLinks => {
					this.caseIntegrationLinks = integrationLinks
				})
			}
		} else {
			this.closeConnection()
		}
	}

	async openCaseConnection () {
		const caseDetails = this.currentCase
		if (caseDetails && caseDetails.id) {
			const connection = await this.hubCaseService.openConnection(caseDetails.id!)
			if (connection) {
				connection.on("caseUpdated", (updatedCase: CaseDetails) => {
					this.$emit("caseUpdated", updatedCase)
				})
				connection.on("caseFieldsUpdated", (RecordFieldsUpdateDto: RecordFieldsUpdateDto) => {
					this.$emit("caseFieldsUpdated", RecordFieldsUpdateDto)
				})
				connection.on("viewingUsers", (viewingUsers: CaseUser[]) => {
					this.$emit("viewingUsersUpdated", viewingUsers)
				})
				connection.on("caseEvents", (caseEvents: CaseEvents) => this.setEvents(caseEvents))
				connection.on("caseNoLongerAvailable", () => {
					this.$emit("caseNoLongerAvailable")
				})
			}
		}
	}

	setEvents (caseEvents: CaseEvents) {
		if (!!caseEvents && !!caseEvents.originatingEvents) {
			caseEvents.originatingEvents.forEach(event => {
				if (event.keyEntries) {
					event.keyEntries.sortBy("created")
				}
			})
		}

		this.caseEvents = caseEvents
	}

	async closeConnection () {
		this.hubCaseService.closeConnection()
	}

	mounted () {
		if (this.currentCase && !this.currentCase.id && this.currentCase.templateId) {
			this.templateChanged(this.currentCase.templateId)
		} else if (this.currentCase && this.currentCase.id) {
			this.currentCaseChanged(this.currentCase, null)
		}
	}

	beforeDestroy () {
		this.closeConnection()
	}

	private async templateChanged (templateId: string) {
		if (this.currentCase && !this.currentCase.id) {
			this.newCaseTemplate = await api.templateDetails(templateId) as TemplateDetails
			this.$emit("templateChanged", this.newCaseTemplate)

			if (this.newCaseTemplate) {
				this.$emit("permissionsChanged", {
					canCreateCase: this.newCaseTemplate.permissions!.case.edit.length === 0 ||
						this.newCaseTemplate.permissions!.case.edit.some((e: any) => this.me.teams.some((mt: any) => mt.id === e.id)),
					canCreateRecord: this.newCaseTemplate.permissions!.record.edit.length === 0 ||
						this.newCaseTemplate.permissions!.record.edit.some((e: any) => this.me.teams.some((mt: any) => mt.id === e.id))
				})
			}
		}
	}

	get titleTemplateEnabled (): boolean {
		return !this.newCaseTemplate || this.newCaseTemplate.enableTitleTemplate === undefined ? false : this.newCaseTemplate.enableTitleTemplate
	}

	get hasMap (): boolean {
		return getCaseHasMapField(this.currentCase)
	}

	get hasEditCasePermissions () {
		if (!this.currentCase) {
			return false
		}

		if (!this.me) {
			return false
		}

		var casePermissions = this.currentCase.permissions
		if (casePermissions && casePermissions.edit && casePermissions.edit.length > 0) {
			if (!(this.me!.teams && casePermissions.edit.some(team => this.me!.teams.find((myTeam: any) => myTeam.id === team.id)))) {
				return false
			}
		}

		return true
	}

	get canEditCurrentCase () {
		if (!this.hasEditCasePermissions) {
			return false
		}

		if (this.currentCase!.isClosed) {
			return this.editAfterClosePermissions && this.makingClosedChanges
		}

		return true
	}

	get canCreateComments () {
		if (!this.hasEditCasePermissions) {
			return false
		}

		if (this.currentCase!.isClosed) {
			return this.editAfterClosePermissions
		}

		return true
	}

	makeClosedChanges () {
		this.makingClosedChanges = true
	}

	caseClose () {
		if (this.currentCase) {
			api.close(this.currentCase.id!)

			if (this.template && this.template.cadenceDetails && this.currentCase.cadenceEnabled &&
				this.template.cadenceDetails.cadencePeriod !== null) {
				var timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
				api.createCadenceSchedule(this.currentCase.id!, timeZone)
			}

			this.$emit("caseClosed")
		}
	}

	public async pushTo (outputOption: IntegrationOutOption) {
		if (!this.currentCase || !this.currentCase.id) return

		const sendingToast = this.$bvToast.toast("Sending...", {
			title: outputOption.integrationTitle,
			variant: "secondary",
			solid: true,
			autoHideDelay: 1000
		})

		try {
			await integrationApi.sendRecord(this.currentCase!.id!, outputOption.integrationKey, outputOption.integrationOut)
			this.$bvToast.toast(`Sent to ${outputOption.integrationOutTitle}`, {
				title: outputOption.integrationTitle,
				variant: "success",
				solid: true,
				autoHideDelay: 3000,
				appendToast: true
			})

			api.caseIntegrationLinks(this.currentCase.id!).then(integrationLinks => {
				this.caseIntegrationLinks = integrationLinks
			})
		} catch {
			this.$bvToast.toast(`Error sending to ${outputOption.integrationOutTitle}`, {
				title: outputOption.integrationTitle,
				variant: "danger",
				solid: true,
				autoHideDelay: 10000,
				appendToast: true
			})
		}
	}
}
